import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isAdmin, isDriver, isFieldRole, isPhleb, ROLES } from './constants/status';

import TopBar from './components/TopBar';
import BottomNav from './components/BottomNav';
import { Toast, PhotoLightbox } from './components/index';

import PhlebView from './views/PhlebView';
import PhlebDetail from './views/PhlebDetail';
import DriverView from './views/DriverView';
import DriverDetail from './views/DriverDetail';
import AdminView from './views/AdminView';
import SettingsPanel from './views/SettingsPanel';
import LoginView from './views/LoginView';
import { getProfile } from './services/authService';
import { setUser, setLocationEnabled } from './store/authSlice';
import Loader from './components/Loader';
import WarningDialog from './components/WarningDialog';
import InstallPrompt from './components/InstallPrompt';
import { updateUserLocation } from './services/locationService';
import { rememberPosition } from './services/lastKnownPosition'; // CLAUDE-SYNC: v1.7.20

export const geoWatchRef = { id: null };

const LOCATION_SEND_THROTTLE_MS = 20000;

function setupSafariVisibilityGuard(isLocationEnabled, role, dispatch, setLocationEnabled, geoWatchRef, updateUserLocation, lastSentRef) {
  const handleVisibility = () => {
    if (window.__cameraOpen) return;
    if (document.visibilityState === 'visible' && isLocationEnabled &&
        (role === 'phleb' || role === 'driver')) {
      if (geoWatchRef.id !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.id);
        geoWatchRef.id = null;
      }
      if (navigator.geolocation) {
        geoWatchRef.id = navigator.geolocation.watchPosition(
          async (pos) => {
            rememberPosition(pos.coords.latitude, pos.coords.longitude); // CLAUDE-SYNC: v1.7.20
            const now = Date.now();
            if (lastSentRef.current && now - lastSentRef.current.ts < LOCATION_SEND_THROTTLE_MS) return;
            const locationPayload = { type: 'Point', coordinates: [pos.coords.longitude, pos.coords.latitude] };
            try {
              await updateUserLocation(locationPayload);
              lastSentRef.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, ts: now };
            } catch (err) {
              console.warn('Location update failed:', err?.message);
            }
          },
          (err) => console.warn('Geolocation error on resume:', err.message),
          { enableHighAccuracy: false, maximumAge: 10000, timeout: 15000 }
        );
      }
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);
  return () => document.removeEventListener('visibilitychange', handleVisibility);
}

function App() {
  const dispatch = useDispatch();
  const role = useSelector((state) => state.auth.role);
  const phlebSelectedId = useSelector((state) => state.stops.selectedStopId);
  const driverSelectedId = useSelector((state) => state.route.selectedStopId);
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const isLocationEnabled = useSelector(state => state.auth.isLocationEnabled);

  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const lastSentRef = useRef(null);

  useEffect(() => {
    if (isLoggedIn) {
      getUserProfile();
    }
  }, [isLoggedIn]);

  // CLAUDE-SYNC: v1.7 — only force Clock Out on explicit permission denial (2026-08-06)
  // Previously ANY getCurrentPosition failure — including GPS timeout (code 3) or
  // position unavailable (code 2), both common indoors at SNF facilities — silently
  // flipped the phleb to "Clocked Out" locally on every page load/reload. This ran
  // on every mount (e.g. when a backgrounded mobile browser tab reloads), matching
  // reports of unexplained clock-outs after periods of inactivity. Now only an
  // explicit permission denial (code 1) changes the clock state; a timed-out or
  // unavailable GPS fix leaves the phleb's current clock-in status untouched.
  const checkLocationPermission = async (userRole) => {
    if (!isFieldRole(userRole) || !navigator.geolocation) return;

    if (navigator.permissions) {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      if (result.state === 'granted') return;
      if (result.state === 'denied') {
        dispatch(setLocationEnabled(false));
        localStorage.setItem("isLocationEnabled", "false");
        return;
      }
    }

    navigator.geolocation.getCurrentPosition(
      () => { },
      (err) => {
        if (err.code === 1) {
          // Permission explicitly denied — force Clock Out.
          dispatch(setLocationEnabled(false));
          localStorage.setItem("isLocationEnabled", "false");
        }
        // err.code 2 (POSITION_UNAVAILABLE) or 3 (TIMEOUT) — leave clock state as-is.
      }
    );
  };

  // ── Only runs when toggle is turned ON by user

  useEffect(() => {
    if (!isAuthenticated || !isFieldRole(role) || !isLocationEnabled) {
      if (geoWatchRef.id !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.id);
        geoWatchRef.id = null;
      }
      return;
    }

    if (!navigator.geolocation) return;

    // Toggle ON → start watching directly (no getCurrentPosition wrapper — that was
    // adding a needless permission round-trip on every effect run and causing the
    // watch to restart on each React re-render)
    geoWatchRef.id = navigator.geolocation.watchPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        rememberPosition(lat, lng); // CLAUDE-SYNC: v1.7.20 — keep latest fix for status/clock events
        const now = Date.now();

        if (lastSentRef.current) {
          if (now - lastSentRef.current.ts < LOCATION_SEND_THROTTLE_MS) return;
        }

        const locationPayload = { type: "Point", coordinates: [lng, lat] };
        try {
          await updateUserLocation(locationPayload);
          lastSentRef.current = { lat, lng, ts: now };
        } catch (err) {
          console.warn('Location update failed:', err?.message);
        }
      },
      (err) => {
        console.warn('Geolocation error:', err.message);
        if (err.code === 1) {
          // Permission denied → force toggle back OFF
          dispatch(setLocationEnabled(false));
        }
      },
      { enableHighAccuracy: false, maximumAge: 10000, timeout: 15000 }
    );


    const cleanupVisibilityGuard = setupSafariVisibilityGuard(
      isLocationEnabled, role, dispatch, setLocationEnabled, geoWatchRef, updateUserLocation, lastSentRef
    );

    return () => {
      if (geoWatchRef.id !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.id);
        geoWatchRef.id = null;
      }
      cleanupVisibilityGuard();
    };
  }, [isAuthenticated, role, isLocationEnabled]);

  const getUserProfile = async () => {
    try {
      const res = await getProfile();
      const userData = res?.data?.user ?? res?.data;
      dispatch(setUser(userData));
      checkLocationPermission(userData.role);
    } catch (error) {
      console.log(error);
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Loader />
        <WarningDialog />
        <LoginView />
      </>
    );
  }

  return (
    <div className="app-shell">
      <Loader />
      <WarningDialog />
      <TopBar />

      {isPhleb(role) && <PhlebView isActive={!phlebSelectedId && !driverSelectedId} />}
      {isPhleb(role) && phlebSelectedId && <PhlebDetail />}
      {isPhleb(role) && !phlebSelectedId && driverSelectedId && <DriverDetail />}

      {isDriver(role) && <DriverView isActive={!driverSelectedId} />}
      {isDriver(role) && driverSelectedId && <DriverDetail />}

      {isAdmin(role) && <AdminView />}
      <PhotoLightbox />
      <SettingsPanel />
      <BottomNav />
      <Toast />
      {/* CLAUDE-SYNC: v1.7.12 — PWA "Add to Home Screen" banner */}
      <InstallPrompt />
    </div>
  );
}

export default App;